<template>
  <div class="app-container">
    <div class="course-resource-header">
      <h2>课程资料管理</h2>
      <div class="header-actions">
        <el-button
          v-if="canUpload"
          type="primary"
          icon="el-icon-upload"
          @click="showUploadDialog = true"
        >
          上传资料
        </el-button>
      </div>
    </div>

    <!-- 课程选择 -->
    <div class="course-selector">
      <el-select
        v-model="selectedCourseId"
        placeholder="请选择课程"
        @change="loadResources"
        style="width: 300px;"
      >
        <el-option
          v-for="course in courses"
          :key="course.id"
          :label="course.courseName"
          :value="course.id"
        />
      </el-select>
    </div>

    <!-- 资料列表 -->
    <div class="resource-list" v-if="selectedCourseId">
      <el-table
        :data="resources"
        v-loading="loading"
        style="width: 100%"
        empty-text="暂无资料"
      >
        <el-table-column prop="originalFileName" label="文件名" min-width="200">
          <template slot-scope="scope">
            <div class="file-info">
              <i :class="getFileIcon(scope.row.fileType)" class="file-icon"></i>
              <span>{{ scope.row.originalFileName }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="description" label="描述" min-width="150">
          <template slot-scope="scope">
            <span v-if="scope.row.description">{{ scope.row.description }}</span>
            <span v-else class="text-muted">暂无描述</span>
          </template>
        </el-table-column>

        <el-table-column prop="fileSize" label="文件大小" width="120">
          <template slot-scope="scope">
            {{ formatFileSize(scope.row.fileSize) }}
          </template>
        </el-table-column>

        <el-table-column prop="downloadCount" label="下载次数" width="100">
          <template slot-scope="scope">
            {{ scope.row.downloadCount || 0 }}
          </template>
        </el-table-column>

        <el-table-column prop="createTime" label="上传时间" width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>

        <el-table-column label="操作" width="200" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              icon="el-icon-download"
              @click="downloadResource(scope.row)"
            >
              下载
            </el-button>
            <el-button
              v-if="canModify(scope.row)"
              type="text"
              icon="el-icon-edit"
              @click="editResource(scope.row)"
            >
              修改
            </el-button>
            <el-button
              v-if="canDelete(scope.row)"
              type="text"
              icon="el-icon-delete"
              @click="deleteResource(scope.row)"
              class="danger-text"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 上传对话框 -->
    <el-dialog
      title="上传课程资料"
      :visible.sync="showUploadDialog"
      width="500px"
      @close="resetUploadForm"
    >
      <el-form :model="uploadForm" :rules="uploadRules" ref="uploadForm" label-width="80px">
        <el-form-item label="选择文件" prop="file">
          <el-upload
            ref="upload"
            :auto-upload="false"
            :on-change="handleFileChange"
            :file-list="fileList"
            :limit="1"
            accept=".pdf,.doc,.docx,.ppt,.pptx,.zip,.rar,.7z"
          >
            <el-button size="small" type="primary">选择文件</el-button>
            <div slot="tip" class="el-upload__tip">
              支持PDF、Word、PPT和压缩包文件，文件大小不超过50MB
            </div>
          </el-upload>
        </el-form-item>

        <el-form-item label="资料描述" prop="description">
          <el-input
            v-model="uploadForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入资料描述（可选）"
          />
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="showUploadDialog = false">取消</el-button>
        <el-button type="primary" @click="uploadResource" :loading="uploading">
          上传
        </el-button>
      </div>
    </el-dialog>

    <!-- 修改对话框 -->
    <el-dialog
      title="修改课程资料"
      :visible.sync="showEditDialog"
      width="500px"
      @close="resetEditForm"
    >
      <el-form :model="editForm" :rules="editRules" ref="editForm" label-width="80px">
        <el-form-item label="资料描述" prop="description">
          <el-input
            v-model="editForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入资料描述"
          />
        </el-form-item>

        <el-form-item label="替换文件" prop="file">
          <el-upload
            ref="editUpload"
            :auto-upload="false"
            :on-change="handleEditFileChange"
            :file-list="editFileList"
            :limit="1"
            accept=".pdf,.doc,.docx,.ppt,.pptx,.zip,.rar,.7z"
          >
            <el-button size="small" type="primary">选择新文件</el-button>
            <div slot="tip" class="el-upload__tip">
              选择新文件将替换原文件，留空则只修改描述
            </div>
          </el-upload>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="updateResource" :loading="updating">
          保存
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getCourseResourceList,
  uploadCourseResource,
  deleteCourseResource,
  updateCourseResource,
  downloadCourseResource
} from '@/api/courseResource'
import { getTeachCourseList } from '@/api/course'
import { mapGetters } from 'vuex'

export default {
  name: 'CourseResourceManagement',
  data() {
    return {
      courses: [],
      selectedCourseId: null,
      resources: [],
      loading: false,
      uploading: false,
      updating: false,

      // 上传相关
      showUploadDialog: false,
      uploadForm: {
        file: null,
        description: ''
      },
      fileList: [],
      uploadRules: {
        file: [
          { required: true, message: '请选择文件', trigger: 'change' }
        ]
      },

      // 修改相关
      showEditDialog: false,
      editForm: {
        id: null,
        description: '',
        file: null
      },
      editFileList: [],
      editRules: {
        description: [
          { required: true, message: '请输入资料描述', trigger: 'blur' }
        ]
      }
    }
  },

  computed: {
    ...mapGetters(['roles']),

    // 是否可以上传（教师和管理员）
    canUpload() {
      return this.roles.some(role =>
        ['role_teacher', 'role_admin', 'role_mteacher'].includes(role)
      )
    }
  },

  mounted() {
    this.loadCourses()
  },

  methods: {
    // 加载课程列表
    async loadCourses() {
      try {
        const response = await getTeachCourseList()
        this.courses = response.data || []
      } catch (error) {
        this.$message.error('加载课程列表失败')
      }
    },

    // 加载资料列表
    async loadResources() {
      if (!this.selectedCourseId) return

      this.loading = true
      try {
        const response = await getCourseResourceList(this.selectedCourseId)
        this.resources = response.data || []
      } catch (error) {
        this.$message.error('加载资料列表失败')
      } finally {
        this.loading = false
      }
    },

    // 文件选择处理
    handleFileChange(file, fileList) {
      this.uploadForm.file = file.raw
      this.fileList = fileList
    },

    handleEditFileChange(file, fileList) {
      this.editForm.file = file.raw
      this.editFileList = fileList
    },

    // 上传资料
    async uploadResource() {
      if (!this.uploadForm.file) {
        this.$message.warning('请选择文件')
        return
      }

      this.uploading = true
      try {
        await uploadCourseResource(
          this.selectedCourseId,
          this.uploadForm.file,
          this.uploadForm.description
        )
        this.$message.success('上传成功')
        this.showUploadDialog = false
        this.loadResources()
      } catch (error) {
        this.$message.error('上传失败: ' + (error.message || '未知错误'))
      } finally {
        this.uploading = false
      }
    },

    // 修改资料
    editResource(resource) {
      this.editForm = {
        id: resource.id,
        description: resource.description || '',
        file: null
      }
      this.editFileList = []
      this.showEditDialog = true
    },

    async updateResource() {
      this.updating = true
      try {
        await updateCourseResource(
          this.editForm.id,
          this.editForm.description,
          this.editForm.file
        )
        this.$message.success('修改成功')
        this.showEditDialog = false
        this.loadResources()
      } catch (error) {
        this.$message.error('修改失败: ' + (error.message || '未知错误'))
      } finally {
        this.updating = false
      }
    },

    // 删除资料
    async deleteResource(resource) {
      try {
        await this.$confirm('确定要删除这个资料吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        await deleteCourseResource(resource.id)
        this.$message.success('删除成功')
        this.loadResources()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败: ' + (error.message || '未知错误'))
        }
      }
    },

    // 下载资料
    async downloadResource(resource) {
      try {
        const response = await downloadCourseResource(resource.id)

        // 创建下载链接
        const blob = new Blob([response])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = resource.originalFileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.$message.success('下载成功')
        this.loadResources() // 刷新下载次数
      } catch (error) {
        this.$message.error('下载失败: ' + (error.message || '未知错误'))
      }
    },

    // 权限检查
    canModify(resource) {
      if (!this.canUpload) return false
      // 这里可以添加更细粒度的权限控制
      return true
    },

    canDelete(resource) {
      if (!this.canUpload) return false
      // 这里可以添加更细粒度的权限控制
      return true
    },

    // 获取文件图标
    getFileIcon(fileType) {
      const iconMap = {
        'pdf': 'el-icon-document',
        'doc': 'el-icon-document',
        'docx': 'el-icon-document',
        'ppt': 'el-icon-present',
        'pptx': 'el-icon-present',
        'zip': 'el-icon-folder',
        'rar': 'el-icon-folder',
        '7z': 'el-icon-folder'
      }
      return iconMap[fileType] || 'el-icon-document'
    },

    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    },

    // 重置表单
    resetUploadForm() {
      this.uploadForm = {
        file: null,
        description: ''
      }
      this.fileList = []
      if (this.$refs.uploadForm) {
        this.$refs.uploadForm.resetFields()
      }
    },

    resetEditForm() {
      this.editForm = {
        id: null,
        description: '',
        file: null
      }
      this.editFileList = []
      if (this.$refs.editForm) {
        this.$refs.editForm.resetFields()
      }
    }
  }
}
</script>

<style scoped>
.course-resource-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.course-selector {
  margin-bottom: 20px;
}

.resource-list {
  margin-top: 20px;
}

.file-info {
  display: flex;
  align-items: center;
}

.file-icon {
  margin-right: 8px;
  font-size: 16px;
  color: #409EFF;
}

.text-muted {
  color: #999;
}

.danger-text {
  color: #F56C6C;
}

.dialog-footer {
  text-align: right;
}

.el-upload__tip {
  color: #999;
  font-size: 12px;
  margin-top: 5px;
}
</style>
